import { useCreation, useLatest, useSize } from 'ahooks';
import { cloneDeep, range } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createModalComponent } from 'react-modal-open';
import { Editor } from '@meitu/saas-editor-core';
import { ChartLayerPureView } from '@meitu/saas-editor-core/es/layers/chart-layer/view/chart-layer';
import type { IChartData, IChartLayer, IChartLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { Button, CommonModal } from '@meitu/xdesign-widgets';
import { useLocaleStore } from '@/i18n';
import { ActionInfo } from '../../types';
import ChartTypeSelector from '../chart-type-selector';
import Table from '../table';
import './index.less';
import { ChartLayer } from '@meitu/saas-editor-core/es/layers/chart-layer';

interface IChartDataEditorProps {
    editor: Editor;
    onSave: (newData: IChartLayerProps) => void;
    onClose?: () => void;
    onAction?: (actionInfo: ActionInfo) => void;
}

const useModalBoundingClientRect = (containerRef: React.RefObject<HTMLDivElement>) => {
    const [rect, setRect] = useState<DOMRect | null>(null);
    useEffect(() => {
        if (!containerRef.current) return;
        setRect(containerRef.current.getBoundingClientRect());
    }, [containerRef]);
    useEffect(() => {
        const handleResize = () => {
            if (!containerRef.current) return;
            setRect(containerRef.current.getBoundingClientRect());
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    return rect;
};

const ChartDataEditor: React.FC<IChartDataEditorProps> = ({ editor, onSave, onClose: onCloseProp, onAction }) => {
    const { $t } = useLocaleStore();
    const initialLayer = useCreation(() => editor.active.getPick(true)! as IChartLayer, []);
    const { activeChartLayerActions } = editor.getInstance(ChartLayer);
    const containerRef = useRef<HTMLDivElement>(null);
    const [previewData, setPreviewData] = useState<IChartLayerProps>(initialLayer.data);
    const [displayPreviewSize, setDisplayPreviewSize] = useState({ row: 20, col: 8 });
    const displayPreviewSizeRef = useLatest(displayPreviewSize);
    const activeChartInfo = useMemo(
        () => editor.getInstance(ChartLayer).getChartInfo({ ...initialLayer, data: previewData }),
        [previewData],
    );
    const previewContainerRef = useRef<HTMLDivElement>(null);
    const previewContainerSize = useSize(previewContainerRef);

    useEffect(() => {
        onAction?.({ target: 'openEditModal' });
    }, []);

    const onClose = () => {
        onCloseProp?.();
        onSave(previewData);
    };

    /** 在chartData基础上补充空白区域 */
    const getDisplayData = (chartData: IChartData) => {
        const result = cloneDeep(chartData);
        const colCount = result[0]?.length ?? 0;

        if (displayPreviewSizeRef.current && result.length < displayPreviewSizeRef.current.row) {
            const getEmptyRow = (): IChartData[number] => new Array(colCount).fill(null);
            const addCount = displayPreviewSizeRef.current.row - result.length;
            range(addCount).forEach(() => {
                result.push(getEmptyRow());
            });
        }

        result.forEach((row) => {
            if (displayPreviewSizeRef.current && row.length < displayPreviewSizeRef.current.col) {
                const addCount = displayPreviewSizeRef.current.col - row.length;
                row.push(...new Array(addCount).fill(null));
            }
        });

        return result;
    };

    /** 去掉末尾的空行空列 */
    const getPureChartData = (chartData: IChartData) => {
        const isEmpty = (item: string | number | null) => item === null || item === '';

        const getRowAndColCount = (chartData: IChartData) => {
            const isEmptyRowStates = chartData.map((row) => row.every(isEmpty));
            const rowCount = isEmptyRowStates.findLastIndex((isEmpty) => !isEmpty) + 1;
            const getColCountPerRow = (row: IChartData[number]) => {
                const isEmptyColStates = row.map((item) => isEmpty(item));
                const colCount = isEmptyColStates.findLastIndex((isEmpty) => !isEmpty) + 1;
                return colCount;
            };
            const colCount = chartData.reduce((acc, row) => Math.max(acc, getColCountPerRow(row)), 0);
            return { rowCount, colCount };
        };

        let result = cloneDeep(chartData);
        const { rowCount, colCount } = getRowAndColCount(chartData);
        result = result.slice(0, rowCount);
        result.forEach((row, rowIndex) => {
            result[rowIndex] = row.slice(0, colCount);
        });
        return result;
    };

    // 防止删除单元格时会误删到图层
    useEffect(() => {
        editor.disableOperate();
        return () => {
            editor.enableOperate();
        };
    }, []);

    const modalRect = useModalBoundingClientRect(containerRef);

    const getPreviewChartScale = () => {
        if (!previewContainerSize) return 1;

        const chartWidth = previewContainerSize.width - 12 * 2;
        const chartHeight = previewContainerSize.height - 12 * 2;

        return Math.min(chartWidth / initialLayer.width, chartHeight / initialLayer.height);
    };

    return (
        <CommonModal open className="chartDataEditorModal">
            <div ref={containerRef} className="chartDataEditorModalInner">
                <header>
                    <div className="modalTitle">{$t((v) => v.chartLayer.panel.editData)}</div>
                    <Button
                        style={{
                            marginLeft: 'auto',
                            width: 100,
                            backgroundColor: 'var(--background-btn-tertiary, #F3F5F7)',
                        }}
                        type="text"
                        size="middle"
                        icon={<IconFont name="TrashCanBold" fontSize={16} color="var(--content-btn-secondary)" />}
                        onClick={() => {
                            const newLayer = activeChartLayerActions.pure.setChartData(
                                { ...initialLayer, data: previewData },
                                [],
                            );
                            setPreviewData(newLayer.data);
                            onAction?.({ target: 'clearData' });
                        }}
                    >
                        {$t((v) => v.chartLayer.panel.clearData)}
                    </Button>
                    <div className="close" onClick={onClose}>
                        <IconFont name="CrossBlack" fontSize={14} color="var(--content-system-tertiary)" />
                    </div>
                </header>

                <div className="modalBody">
                    <div className="modalBodyLeft">
                        <ChartTypeSelector
                            chartType={activeChartInfo.type}
                            onChangeChartType={(newChartType) => {
                                const newLayer = activeChartLayerActions.pure.changeChartType(
                                    { ...initialLayer, data: previewData },
                                    newChartType,
                                );
                                setPreviewData(newLayer.data);
                            }}
                            onTrack={(type) =>
                                onAction?.({ target: 'chartType', data: { actionType: type, isFromEditModal: true } })
                            }
                        />

                        <div className="chart-preview" ref={previewContainerRef}>
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    width: initialLayer.width,
                                    height: initialLayer.height,
                                    transform: `translate(-50%, -50%) scale(${getPreviewChartScale()})`,
                                    transformOrigin: 'center center',
                                }}
                            >
                                <ChartLayerPureView layer={{ ...initialLayer, data: previewData }} />
                            </div>
                        </div>
                    </div>

                    <div className="chartDataEditor">
                        <Table
                            fixCSSInfo={{ top: -1 * (modalRect?.top ?? 0), left: -1 * (modalRect?.left ?? 0) }}
                            data={getDisplayData(previewData.chartData)}
                            colorAxis={activeChartInfo.type.includes('pie') ? 'categoryAxis' : 'valueAxis'}
                            colors={previewData.colors}
                            onChangeColors={(newColors) => {
                                const newLayer = activeChartLayerActions.pure.setColorsPreset(
                                    { ...initialLayer, data: previewData },
                                    newColors,
                                );
                                setPreviewData(newLayer.data);
                            }}
                            onChangeData={(newData) => {
                                const newRowCount = newData.length;
                                const newColCount = newData[0].length;
                                setDisplayPreviewSize((oldSize) => {
                                    const newSize = { ...oldSize };
                                    if (oldSize.row < newRowCount) {
                                        newSize.row = newRowCount;
                                    }
                                    if (oldSize.col < newColCount) {
                                        newSize.col = newColCount;
                                    }
                                    return newSize;
                                });
                                const newChartData = getPureChartData(newData);
                                const newLayer = activeChartLayerActions.pure.setChartData(
                                    { ...initialLayer, data: previewData },
                                    newChartData,
                                );
                                setPreviewData(newLayer.data);
                            }}
                            onAction={onAction}
                        />
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};

ChartDataEditor.displayName = 'ChartDataEditor';

export const openChartDataEditor = (args: IChartDataEditorProps) => createModalComponent(ChartDataEditor).open(args);
